(function () {
  /* OpenAI (ChatGPT) Ads measurement — loads the oaiq SDK and fires the
     storefront browse events: page_viewed, contents_viewed (PDP) and
     items_added. Checkout and purchase events belong to the custom pixel and
     the Conversions Hub, never to this file.

     Runs ONLY under Cookiebot marketing consent: the SDK script is never
     inserted before it, because the SDK itself captures ?oppref= from the
     landing URL into the __oppref cookie (30 d) and mints __obref (365 d).

     SDK contract (developers.openai.com/ads/measurement-pixel):
       - loader shim + async https://bzrcdn.openai.com/sdk/oaiq.min.js, then
         oaiq("init", { pixelId, user? }); user = sha256 hex of the normalised
         value ("email ... after trimming whitespace and converting it to
         lowercase"; external_id "a stable, pseudonymous customer identifier").
       - oaiq("measure", name, data, { event_id }).
       - oaiq("consent", false): "doesn't send measurement-event pings" and
         "removes both cookies"; true "allows future measurement events;
         blocked events aren't replayed".
     Events (developers.openai.com/ads/supported-events): all three take the
     `contents` data type; amounts are integers in ISO 4217 minor units, which
     is what Liquid prices and the /cart/add.js response already carry. */

  /* wrangler.toml OPENAI_PIXEL_ID in myrqvist-conversions-hub — mirror, never diverge. */
  var OAI_PIXEL_ID = '9rK8nA4nFSS2WsZnPBYpMy';
  var OAI_SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';

  var loaded = false;
  var revoked = false;
  var pageTracked = false;

  function hasMarketingConsent() {
    return !!(typeof Cookiebot !== 'undefined' && Cookiebot.consent && Cookiebot.consent.marketing);
  }

  function makeEventId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    /* UUID v4 fallback for browsers without crypto.randomUUID (older Safari). */
    var seed = Date.now();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (seed + Math.random() * 16) % 16 | 0;
      seed = Math.floor(seed / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /* Vendor code must never be able to break the page. */
  function call() {
    try { window.oaiq.apply(window, arguments); } catch (e) {}
  }

  function measure(name, data) {
    call('measure', name, data, { event_id: makeEventId() });
  }

  function sha256Hex(value) {
    return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)).then(function (buf) {
      return Array.prototype.map.call(new Uint8Array(buf), function (b) {
        return ('0' + b.toString(16)).slice(-2);
      }).join('');
    });
  }

  /* User data is request-scoped, so init{user} must precede the measure it
     should match; hashing may delay page_viewed by at most this much. Mirrors
     IDENTITY_WAIT_MS in the hub's pixels/openai.js. */
  var IDENTITY_WAIT_MS = 250;

  /* Logged-in match keys, set by the Gorgias block in theme.liquid (empty
     strings when logged out). Returns null when there is nothing to hash, so
     anonymous shoppers stay synchronous; otherwise a promise of the user
     object, or of null on timeout/error — it never rejects. */
  function hashedUser() {
    try {
      var email = String(window.SHOPIFY_CUSTOMER_EMAIL || '').trim().toLowerCase();
      var id = String(window.SHOPIFY_CUSTOMER_ID || '').trim();
      if ((!email && !id) || !(window.crypto && window.crypto.subtle)) return null;
      var hashes = Promise.all([email ? sha256Hex(email) : null, id ? sha256Hex(id) : null])
        .then(function (h) {
          var user = {};
          if (h[0]) user.email_sha256 = h[0];
          if (h[1]) user.external_id_sha256 = h[1];
          return user;
        });
      var timeout = new Promise(function (resolve) { setTimeout(function () { resolve(null); }, IDENTITY_WAIT_MS); });
      return Promise.race([hashes, timeout]).catch(function () { return null; });
    } catch (e) {
      return null;
    }
  }

  function ensureOaiq() {
    if (loaded) return;
    loaded = true;
    try {
      if (!window.oaiq) {
        var q = function () { q.q.push(arguments); };
        q.q = [];
        window.oaiq = q;
        var js = document.createElement('script');
        js.async = true;
        js.src = OAI_SDK_URL;
        var f = document.getElementsByTagName('script')[0];
        f.parentNode.insertBefore(js, f);
      }
    } catch (e) { return; }
    /* Only reached with marketing consent. Set before init: a denial stored
       by an earlier session persists, and would otherwise keep a shopper who
       declined and later accepted dark. */
    call('consent', true);
    call('init', { pixelId: OAI_PIXEL_ID });
  }

  function trackPage() {
    if (pageTracked) return;
    pageTracked = true;
    var pending = hashedUser();
    if (!pending) {
      measurePage();
      return;
    }
    /* Consent can be withdrawn during the identity wait. */
    pending.then(function (user) {
      if (revoked || !hasMarketingConsent()) return;
      if (user) call('init', { pixelId: OAI_PIXEL_ID, user: user });
      measurePage();
    });
  }

  function measurePage() {
    measure('page_viewed', { type: 'contents' });
    var p = window.__oaiProduct;
    if (p) {
      measure('contents_viewed', {
        type: 'contents',
        amount: p.price,
        currency: p.currency,
        contents: [{ id: String(p.variantId), name: p.title, quantity: 1, amount: p.price }],
      });
    }
  }

  /* Cookiebot fires CookiebotOnAccept for any accept, including one that
     leaves marketing off, so both events re-read the category rather than
     trusting which event fired. */
  function sync() {
    if (hasMarketingConsent()) {
      ensureOaiq();
      if (revoked) {
        revoked = false;
        call('consent', true);
      }
      trackPage();
    } else if (loaded && !revoked) {
      revoked = true;
      call('consent', false);
    }
  }

  document.addEventListener('cart:item-added', function (e) {
    try {
      if (!loaded || revoked || !hasMarketingConsent()) return;
      var d = e && e.detail;
      var item = d && d.item;
      if (!item || !item.variant_id) return;
      measure('items_added', {
        type: 'contents',
        amount: item.final_price * d.quantity,
        currency: window.__oaiCurrency,
        contents: [{ id: String(item.variant_id), name: item.product_title, quantity: d.quantity, amount: item.final_price }],
      });
    } catch (err) {}
  });

  window.addEventListener('CookiebotOnConsentReady', sync);
  window.addEventListener('CookiebotOnAccept', sync);
  window.addEventListener('CookiebotOnDecline', sync);
  sync();
})();
